import { useGlobalInfoStore } from '@/stores/Global';
import { reqCatch } from '@/utils/catch';
import { getMemberByEnterprise } from '@/api/org';
import { getUserAuth } from '@/api/user';
import { useAccountStore } from '@/stores/user';
import { orgInfoStore } from '@/stores/Org';
import { enterpriseInfoStore } from '@/stores/enterprise';
import { getEnterpriseDetail, type EnterpriseDetailRes } from '@/api/org/setting';
import { useRoute } from 'vue-router';
import { repoInfoStore } from '@/stores/Repo';
import { getUserProfile } from '@/api/user';
import { checkEnterpriseAuth } from '@/utils/auth';
import { ENTERPRISE_SETTING_PAGE_TYPE } from '@/constant/route';
import { setting } from '@/setting';
import { AuthMap } from '@/constant';
/**
 *  * 验证用户在组织或项目下的权限
 * id - 组织/项目路径
 */
export enum permissionType {
  ORG_TYPE = 'org',
  REPO_TYPE = 'repo'
}
export async function checkPermission(type: permissionType, id: string) {
  const account = useAccountStore();
  if (!account.isLogin) {
    account.saveUpdateAccessStatus(true);
    return {};
  }

  const globalStore = useGlobalInfoStore();
  const params = {
    path: id
  };
  const sourceStore = type === permissionType.ORG_TYPE ? orgInfoStore() : repoInfoStore();
  const res = await reqCatch(getUserAuth, params);
  if (!res.error) {
    const { resource_trees, role_info } = res.data.data;
    account.setAccess(resource_trees); // 保存用户权限树
    account.saveRoleInfo(role_info); // 保存用户权限树
    sourceStore.setAccessLevel(role_info?.access_level || 0); // 保存用户在组织/项目中的access_level
    account.saveUpdateAccessStatus(true);
    return {
      data: res.data.data,
      error: null
    };
  } else {
    account.saveUpdateAccessStatus(true);
    const errorCode = res.error?.error_code;
    if (errorCode === 403) {
      // 无组织/项目权限，显示403
      sourceStore.setAccessLevel(0);
      sourceStore.setVisibility('private');
      globalStore.setIsNoAccess(true);
    }
    return {
      data: null,
      error: res.error
    };
  }
}

/**
 * 更新用户信息
 */
export async function checkUserInfo(to?: any) {
  const route = to || useRoute();
  const namespace = route.params?.namespace;
  const username = typeof namespace === 'string' ? namespace : namespace?.join('');
  const res = await reqCatch(getUserProfile, { username: username || '' });
  if (!res.error) {
    const { data } = res.data;
    return {
      data,
      error: null
    };
  } else {
    const errorCode = res?.error?.error_code;
    if (errorCode === 404) {
      // 审核未通过，后端返回404
      location.replace('/404');
    }
    if (errorCode === 451) {
      // 待审核
      location.replace('/451');
    }
    return {
      data: null,
      error: res.error
    };
  }
}

/*
 * 检查企业成员
 */
export async function checkEnterpriseMember(enterpriseId: string, route: Record<string, any>) {
  const account = useAccountStore();
  const res = await reqCatch(getMemberByEnterprise, { enterpriseId: enterpriseId });
  const data = res?.data?.data?.data;
  let isEnterMember = false;
  const level = data?.role_info?.access_level || 0;
  const mgntMode = data?.role_info?.mgnt_mode || 0;
  if (data) {
    isEnterMember = level >= 10;
    if (isEnterMember) {
      account.setEnterpriseAccess(data?.resource_trees || []);
      account.setEnterpriseRole(data?.role_info?.cn_name || '');
    }
  }
  account.saveIsEnterpriseMember(isEnterMember, level, mgntMode);
  let flag = true;
  if (route.name === 'enterpriseIssueList' && level < 50) {
    // 如果有自定义权限并且是渗透模式则也运行访问
    flag = checkEnterpriseAuth(AuthMap.issue.update) && mgntMode === 1;
  }
  // 如果无对应的企业菜单权限则进入到404
  if (
    route.meta.pType === ENTERPRISE_SETTING_PAGE_TYPE &&
    (level < setting.role[route.meta.permission] || !isEnterMember)
  ) {
    flag = false;
  }
  if (!flag) {
    const globalStore = useGlobalInfoStore();
    globalStore.setIsNotFound(true);
  }
  account.saveUpdateAccessStatus(true);
}

export async function checkEnterpriseInfo(
  to: any,
  haveInitEnterprise: boolean,
  options?: { deferSetFinished?: boolean }
) {
  const route = to || useRoute();
  const enterpriseStore = enterpriseInfoStore();
  const globalStore = useGlobalInfoStore();
  const deferFinished = Boolean(options?.deferSetFinished);

  if (haveInitEnterprise) {
    enterpriseStore.setEnterpriseFinished(true);
    return {
      data: null,
      error: null
    };
  }

  const namespace = route.params.namespace as string;
  if (!namespace) {
    enterpriseStore.setEnterpriseFinished(true);
    globalStore.setIsNotFound(true);
    return {
      data: null,
      error: null
    };
  }

  enterpriseStore.setEnterpriseFinished(false);
  let error: unknown = null;
  try {
    const res = await getEnterpriseDetail(namespace);
    if (!res.error) {
      const d = (res.data as { data?: EnterpriseDetailRes } | null | undefined)?.data;
      if (d) {
        enterpriseStore.setEnterpriseInfo(d);
      }
    } else {
      error = res.error;
      const errorCode = (res.error as { error_code?: number })?.error_code;
      if (errorCode === 404) {
        globalStore.setIsNotFound(true);
      }
      if (errorCode === 403) {
        globalStore.setIsNoAccess(true);
      }
      if (errorCode === 451) {
        globalStore.setIsPendingReview(true);
      }
    }
    if (!deferFinished) {
      enterpriseStore.setEnterpriseFinished(true);
    }
  } catch (err) {
    error = err;
    if (!deferFinished) {
      enterpriseStore.setEnterpriseFinished(true);
    }
  }

  return {
    data: null,
    error
  };
}
